export type StaffClickConfig = {
    open_url?: string | null;
    parameter?: string | null;
    allowed_hosts?: string[] | null;
};

let activeConfig: StaffClickConfig | null = null;
let installed = false;

function text(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

function normalizedHosts(config?: StaffClickConfig | null): string[] {
    return Array.isArray(config?.allowed_hosts)
        ? Array.from(new Set(config.allowed_hosts.map((host) => text(host).trim().toLowerCase()).filter(Boolean)))
        : [];
}

export function setStaffClickConfig(config?: StaffClickConfig | null): void {
    activeConfig = config || null;
}

export function staffClickHref(href: string, config: StaffClickConfig | null = activeConfig): string {
    if (typeof window === 'undefined') return href;

    const openUrl = text(config?.open_url).trim();
    const parameter = text(config?.parameter).trim() || 'lp_staff_click';
    const allowedHosts = normalizedHosts(config);
    if (!openUrl || allowedHosts.length === 0) return href;

    try {
        const target = new URL(href, window.location.href);
        if (!['http:', 'https:'].includes(target.protocol)) return href;
        if (target.searchParams.has(parameter)) return href;

        const host = target.hostname.toLowerCase();
        if (!allowedHosts.includes(host)) return href;

        const internal = new URL(openUrl, window.location.origin);
        internal.searchParams.set('url', target.toString());

        return internal.toString();
    } catch {
        return href;
    }
}

function anchorFromEventTarget(target: EventTarget | null): HTMLAnchorElement | null {
    if (!(target instanceof Element)) return null;

    return target.closest<HTMLAnchorElement>('a[href]');
}

function onDocumentClick(event: MouseEvent): void {
    if (event.defaultPrevented || event.button !== 0) return;

    const anchor = anchorFromEventTarget(event.target);
    if (!anchor || anchor.hasAttribute('download') || anchor.dataset.lpStaffClick === 'off') return;

    const originalHref = anchor.href;
    const nextHref = staffClickHref(originalHref);
    if (nextHref === originalHref) return;

    anchor.href = nextHref;
    window.setTimeout(() => {
        if (anchor.href === nextHref) {
            anchor.href = originalHref;
        }
    }, 0);
}

export function installStaffClickLinkGuard(config?: StaffClickConfig | null): void {
    setStaffClickConfig(config);
    if (installed || typeof document === 'undefined') return;

    document.addEventListener('click', onDocumentClick, true);
    installed = true;
}
